import { MapPinIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
  usePlacesAutocomplete,
  type PlacePrediction,
} from '@/hooks/usePlacesAutocomplete';
import type { PickedEventPlace } from '@/lib/eventLocation';

interface LocationInputProps {
  address: string;
  note: string;
  onAddressChange: (text: string) => void;
  onNoteChange: (text: string) => void;
  onPlaceChange: (place: PickedEventPlace | null) => void;
  inputClassName: string;
}

export default function LocationInput({
  address,
  note,
  onAddressChange,
  onNoteChange,
  onPlaceChange,
  inputClassName,
}: LocationInputProps) {
  const {
    predictions,
    isSearching,
    isFetchingDetails,
    error,
    hasApiKey,
    handleSearchChange,
    selectPrediction,
  } = usePlacesAutocomplete();

  const handleAddressChange = (text: string) => {
    onAddressChange(text);
    onPlaceChange(null);
    handleSearchChange(text);
  };

  const handlePredictionSelect = async (prediction: PlacePrediction) => {
    const parsed = await selectPrediction(prediction);
    if (!parsed) return;
    onAddressChange(parsed.formattedAddress);
    onPlaceChange({
      placeId: parsed.placeId,
      formatted: parsed.formattedAddress,
      latitude: parsed.latitude,
      longitude: parsed.longitude,
    });
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <label className="mb-1 block text-xs font-medium text-flock-navy-600">
          Address or place
        </label>
        <div className="relative">
          {hasApiKey ? (
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-flock-navy-400" />
          ) : (
            <MapPinIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-flock-navy-400" />
          )}
          <input
            value={address}
            onChange={(e) => handleAddressChange(e.target.value)}
            placeholder="Search or type a location"
            className={`${inputClassName} pl-9`}
            autoComplete="off"
          />
          {(isSearching || isFetchingDetails) && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-flock-navy-400">
              {isFetchingDetails ? 'Getting address…' : 'Searching…'}
            </span>
          )}
        </div>
        {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
        {predictions.length > 0 && (
          <ul className="mt-1 max-h-60 w-full overflow-auto rounded-xl border border-flock-gray-200 bg-white shadow-lg">
            {predictions.map((prediction) => (
              <li key={prediction.place_id}>
                <button
                  type="button"
                  onClick={() => handlePredictionSelect(prediction)}
                  className="flex min-h-11 w-full items-start gap-2 px-3 py-2 text-left hover:bg-flock-navy-50"
                >
                  <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-flock-navy-400" />
                  <span>
                    <span className="block text-sm font-medium text-flock-navy-800">
                      {prediction.structured_formatting.main_text}
                    </span>
                    <span className="block text-xs text-flock-navy-400">
                      {prediction.structured_formatting.secondary_text}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-flock-navy-600">
          Room or building (optional)
        </label>
        <input
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder="e.g. C158, Fellowship Hall"
          className={inputClassName}
        />
      </div>
    </div>
  );
}
